import { CellEditRequestEvent, ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React from "react";
import { ISSUE_COMPLETED_VALUE, ISSUE_EXPIRED, ISSUE_STATES_OPTIONS } from "../../app/routes/issuesApp/issueConstants";
import { useCreateIssueMutation, useDoUpdateIssueMutation } from "../../app/routes/issuesApp/issueRTKLegacy";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { ClickableTableIdButton, CreateNewButton, DisplayDueDate, FailedAlert } from "../index";

import { AG_GRID_LOCALE_NOR } from "../../agGridLocale";
import { isValidDateValue } from "../../app/routes/admin/selectors";
import { addAppModalThunk } from "../../global/globalSlice";
import { ActionIcon } from "../CustomIcons/CustomIcons";
import { StatusBadgeRenderer, UserCellRenderer } from "./ConnectedIssues";
import { AppModalEnums } from "@mcapps/shared/sharedConstants";
import { IIssueApi } from "@schemas/issueSchema";
import { Button, Text } from "@mantine/core";
import { IconPlus } from "@tabler/icons-react";

const getIssueBoardId = (issueBoard: IIssueApi["issueBoard"]): string =>
  typeof issueBoard === "string" ? issueBoard : issueBoard?._id;

const getIssueProjectId = (issueProject: IIssueApi["project"], fallbackProjectId?: string): string | undefined =>
  typeof issueProject === "string" ? issueProject : (issueProject?._id ?? fallbackProjectId);

export const TaskForAIssueTable: React.FC<{ issue: IIssueApi }> = ({ issue }) => {
  const dispatch = useAppDispatch();

  const project = useAppSelector((state) => state.adminReducer.project);

  const [updateIssue, { isError }] = useDoUpdateIssueMutation();

  const [addNewIssue, { isLoading, isSuccess }] = useCreateIssueMutation();

  const columnDefs: ColDef<IIssueApi, any>[] = [
    {
      field: "id",
      width: 120,
      headerName: "Id",
      cellRenderer: (props) => {
        const { node, api, data } = props;
        return (
          <>
            <ActionIcon
              className="text-secondary"
              style={{ marginRight: "3px", fontSize: "1.1em", marginBottom: "3px" }}
            />
            <ClickableTableIdButton
              id={data?.id}
              _id={data?._id}
              setItem={() =>
                dispatch(
                  addAppModalThunk({
                    itemId: data?._id,
                    boardId: data?.issueBoard,
                    projectId: data?.project,
                    app: AppModalEnums.ISSUE,
                  }),
                )
              }
            />
          </>
        );
      },
    },
    {
      field: "title",
      headerName: "Tittel",
      flex: 1,
      minWidth: 150,
      editable: true,
    },
    {
      field: "assignedTo",
      headerName: "Ansv. for utførelse",
      editable: true,
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: project.members.map((m) => m.user),
        cellRenderer: (params) => <UserCellRenderer height="15px" user={params.value} />,
      },
      cellRenderer: (params) => (
        <UserCellRenderer
          user={params.data?.assignedTo}
          onClear={() => {
            if (params.data == null) {
              return;
            }

            const resolvedProjectId = getIssueProjectId(params.data.project, project?._id);
            if (resolvedProjectId == null) {
              return;
            }

            updateIssue({
              projectId: resolvedProjectId,
              issueId: params.data._id,
              attr: "assignedTo",
              value: null,
              oldValue: params.value?.name ?? "",
              issueBoardId: getIssueBoardId(params.data.issueBoard),
            });
          }}
        />
      ),
    },
    {
      field: "dates.due",
      cellEditor: "agDateCellEditor",
      width: 150,
      headerName: "Tidsfrist",
      editable: true,
      filter: "agDateColumnFilter",
      valueGetter: (params) => {
        return params?.data?.dates?.due === null ? null : new Date(params.data?.dates?.due);
      },
      valueSetter: (params) => {
        return params.newValue.toISOString();
      },
      cellRenderer: (params) => {
        if (isValidDateValue(params?.value) === false) {
          return "-";
        }
        return (
          <DisplayDueDate
            dueDate={params?.value}
            isComplete={
              params.data != null &&
              (params.data.status === ISSUE_COMPLETED_VALUE || params.data.status === ISSUE_EXPIRED)
            }
          />
        );
      },
    },
    {
      field: "status",
      valueGetter: (params) => {
        const status = ISSUE_STATES_OPTIONS.find((opt) => opt.value === params.data.status);
        return status;
      },
      valueSetter: (params) => {
        return params.newValue.value;
      },
      cellEditor: "agRichSelectCellEditor",
      cellEditorParams: {
        values: ISSUE_STATES_OPTIONS,
        cellRenderer: StatusBadgeRenderer,
        valueListMaxHeight: 220,
      },
      headerName: "Status",
      editable: true,
      width: 150,
      cellRenderer: StatusBadgeRenderer,
    },
  ];

  const onCellEditRequest = (event: CellEditRequestEvent<IIssueApi>) => {
    const issue = event.data;
    const field = event.colDef.field;

    if (issue == null || field == null) {
      return;
    }

    let updateValue;

    if (field.startsWith("assignedTo")) {
      updateValue =
        typeof event.newValue === "string"
          ? (project.members.find((member) => member.user?._id === event.newValue)?.user ?? null)
          : (event.newValue ?? null);
    } else if (field.startsWith("status")) {
      updateValue = event.newValue.value;
    } else if (field.startsWith("dates")) {
      if (event.newValue == null) {
        updateValue = "";
      } else {
        updateValue = event.newValue;

        if (updateValue.toISOString() === event.oldValue?.toISOString()) {
          return;
        }
      }
    } else {
      updateValue = event.newValue;
    }

    const resolvedProjectId = getIssueProjectId(issue.project, project?._id);
    if (resolvedProjectId == null) {
      return;
    }

    updateIssue({
      projectId: resolvedProjectId,
      issueId: issue._id,
      attr: field,
      value: updateValue,
      oldValue: event.oldValue?.name ?? "",
      issueBoardId: getIssueBoardId(issue.issueBoard),
    });
  };

  return (
    <div className="p-2">
      {isError === true && <FailedAlert />}
      <div className="d-flex align-items-center" style={{ marginBottom: "4px" }}>
        <Text size="1.3em" mr="xs">
          Aksjoner
        </Text>
        <Button
          size="compact-xs"
          variant="outline"
          onClick={() =>
            addNewIssue({
              projectId: project?._id,
              issueBoardId: issue.issueBoard as string,
              issueTitle: "",
              taskOrIssue: "TASK",
              issueIdIfItIsATask: issue._id,
            })
          }
          tooltip="Ny aksjon"
          disabled={isLoading}
        >
          <IconPlus size={16} />
        </Button>
      </div>
      {issue.connectedTasks?.length > 0 && (
        <AgGridReact
          defaultColDef={{ sortable: false, suppressHeaderMenuButton: false }}
          domLayout={"autoHeight"}
          columnDefs={columnDefs}
          getRowId={(p) => p.data._id}
          rowData={issue.connectedTasks as IIssueApi[]}
          stopEditingWhenCellsLoseFocus={true}
          animateRows={true}
          readOnlyEdit={true}
          onCellEditRequest={onCellEditRequest}
          suppressContextMenu={true}
          detailRowAutoHeight={true}
          localeText={AG_GRID_LOCALE_NOR}
        />
      )}
    </div>
  );
};
